<template>
  <section v-if="state !== 'hidden'" class="bg-surface-0 p-6 border rounded-md border-surface-200"
    :data-testid="`${testIdPrefix}-custom-fields`">
    <div class="flex items-center justify-between mb-4">
      <h3 class="font-semibold text-lg text-surface-700">Additional information</h3>
      <Button v-if="canEdit && state === 'ready'" label="Edit" icon="pi pi-pencil" size="small" outlined @click="openEdit"
        data-testid="btn-edit-custom-fields" />
    </div>

    <div v-if="state === 'loading'" class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3" data-testid="custom-fields-loading">
      <Skeleton v-for="n in 4" :key="n" height="1.25rem" />
    </div>

    <p v-else-if="state === 'error'" class="text-sm text-red-500" data-testid="custom-fields-error">
      Could not load additional information. Refresh the page to try again.
    </p>

    <div v-else-if="state === 'empty'" class="text-sm text-surface-500 flex flex-wrap items-center gap-2"
      data-testid="custom-fields-empty">
      <span>Your school hasn't added any custom fields for {{ entityCopy.label.toLowerCase() }} yet.</span>
      <RouterLink :to="{ name: 'admin-settings', query: { tab: 'custom_fields' } }" class="text-primary font-medium"
        data-testid="link-setup-custom-fields">
        Set up custom fields
      </RouterLink>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-md">
      <p v-for="entry in values" :key="entry.field.id" :data-testid="`custom-field-value-${entry.field.key}`">
        <strong>{{ entry.field.label }}:</strong> {{ formatCustomFieldValue(entry.field, entry.value) }}
        <small v-if="filledByStudentText(entry)" class="block text-xs text-surface-500"
          :data-testid="`custom-field-filled-by-${entry.field.key}`">{{ filledByStudentText(entry) }}</small>
      </p>
    </div>

    <Dialog v-model:visible="editing" header="Edit additional information" modal
      :style="{ width: '36rem', maxWidth: '95vw' }">
      <form @submit.prevent="save">
        <CustomFieldsFormSection v-model="model" :entries="values" :errors="fieldErrors" :idPrefix="`cf-${testIdPrefix}`" />
      </form>
      <template #footer>
        <Button label="Cancel" severity="secondary" text @click="editing = false" />
        <Button label="Save" icon="pi pi-check" :loading="saving" @click="save" data-testid="btn-save-custom-fields" />
      </template>
    </Dialog>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue';
import { useToast } from 'primevue/usetoast';
import type { CustomFieldEntity } from '@/types/composition-functions';
import { useFormErrors } from '@/composables/useFormErrors';
import { useEntityCustomFieldValues } from '@/composables/queries/useEntityCustomFieldValues';
import { useCustomFieldMutations } from '@/composables/mutations/useCustomFieldMutations';
import { useUserStore } from '@/store/userStore';
import { AppPermissions, can } from '@/utils/permissions';
import CustomFieldsFormSection from '@/components/common/CustomFieldsFormSection.vue';
import {
  buildEditModel, buildValuesPayload, customFieldEntityCopy, customFieldCardState, formatCustomFieldValue,
  missingRequiredErrors, valueErrorKey, type CustomFieldInputValue,
} from '@/utils/customFields';
import { filledByStudentText } from '@/utils/studentProfileForm';

/**
 * The school's custom fields for one record — a student, staff member or
 * guardian (#574). Shows a skeleton while loading, a message if loading
 * fails, and — only for people who can create fields — a "Set up custom
 * fields" prompt when the school has none; everyone else sees nothing then.
 * The parent decides edit permission, since it differs per entity.
 */
const props = defineProps<{
  entity: CustomFieldEntity;
  entityId: string | number;
  canEdit: boolean;
}>();

const toast = useToast();
const userStore = useUserStore();
const { values, loading, error, refetch } = useEntityCustomFieldValues(props.entity, toRef(props, 'entityId'));
const { saveEntityValues } = useCustomFieldMutations();
const { serverErrors, handleServerErrors, clearErrors, getFirstErrorMessage } = useFormErrors();

const testIdPrefix = computed(() => props.entity.toLowerCase());
const entityCopy = computed(() => customFieldEntityCopy(props.entity));
const canSetUp = computed(() =>
  can(userStore.currentUser, AppPermissions.CustomFields.Actions.Create, AppPermissions.CustomFields.Resource),
);
const state = computed(() => customFieldCardState({
  loading: !!loading.value,
  failed: !!error.value,
  count: values.value.length,
  canSetUp: canSetUp.value,
}));

watch(error, (value) => {
  if (value) console.error('Could not load custom field values', value);
});

const editing = ref(false);
const saving = ref(false);
const model = ref<Record<string, CustomFieldInputValue>>({});
const clientErrors = ref<Record<string, string>>({});

/** Client-side required errors first, then the backend's `values.<key>` messages. */
const fieldErrors = computed<Record<string, string>>(() => Object.fromEntries(values.value.map(({ field }) => [
  field.key,
  clientErrors.value[field.key] ?? serverErrors.value[valueErrorKey(field.key)]?.[0] ?? '',
])));

function openEdit() {
  model.value = buildEditModel(values.value);
  clientErrors.value = {};
  clearErrors();
  editing.value = true;
}

async function save() {
  clearErrors();
  clientErrors.value = missingRequiredErrors(values.value, model.value);
  if (Object.keys(clientErrors.value).length > 0) return;

  const payload = buildValuesPayload(values.value, model.value);
  if (payload.length === 0) {
    editing.value = false;
    return;
  }

  saving.value = true;
  try {
    await saveEntityValues(props.entity, String(props.entityId), payload);
    await refetch();
    toast.add({ severity: 'success', summary: 'Saved', detail: 'Additional information updated.', life: 3000 });
    editing.value = false;
  } catch (err) {
    handleServerErrors(err);
    // Field errors show inline; anything else (permission, a field removed
    // meanwhile) needs a toast or the dialog would fail silently.
    if (!Object.values(fieldErrors.value).some(Boolean)) {
      console.error(err);
      toast.add({
        severity: 'error',
        summary: 'Could not save',
        detail: getFirstErrorMessage() || (err as Error)?.message || 'Please try again.',
        life: 5000,
      });
    }
  } finally {
    saving.value = false;
  }
}
</script>
